import { graphql } from 'gatsby';

import { styled } from '@mui/material';

import { imagePopulate } from '../../../../libs/strapi-populates';
import { ArticleTeaserMedium } from '../../blog/components/article-teaser-medium';
import { StrapiBlogArticle } from '../../blog/types';
import {
  ComponentProps,
  ConfigType,
  ContentSectionItem,
  PossibleContentSections,
} from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';

// Impossible to use the StrapiBlogArticleFragment here in article as this would
// result in infinite nesting due to the fact that StrapiBlogArticle spreads
// the ArticleReferenceFragment which itself again spreads StrapiBlogArticle.
// Therefore, only the needed fields are queried here.
export const query = graphql`
  fragment ArticleReferenceFragment on STRAPI__COMPONENT_SHARED_ARTICLE_REFERENCE {
    title
    article {
      title
      author {
        ...AuthorBoxFragment
      }
      excerpt
      date

      responsiveCoverImage {
        ...StrapiImageFragment
      }
    }
    strapi_component
  }
`;

export const componentName = 'shared.article-reference';

export type ArticleReferenceData = {
  article: StrapiBlogArticle;
  title?: string | null;
  strapi_component: typeof componentName;
};

export type ArticleReferencePreviewData = ArticleReferenceData;

export const isComponent = (
  component: PossibleContentSections,
): component is ContentSectionItem<ArticleReferenceData> =>
  component.strapi_component === componentName;

const Component = (props: ComponentProps<ArticleReferenceData>) => (
  <div>
    {props.title ? <Headline>{props.title}</Headline> : null}
    <StyledArticleTeaser
      article={props.article}
      lang={props.lang}
      langPrefix={props.langPrefix}
      blogSlug={props.navigationLinks.blog.generalSlug.slug}
      locale={props.locale}
      translations={props.translations}
    />
  </div>
);

export const config: ConfigType<ArticleReferenceData> = {
  componentName,
  isComponent,
  Component,
};

export const previewConfig: ContentLivePreviewConfig = {
  componentName,
  populationParams: {
    article: {
      populate: {
        author: { populate: { name: '*', image: '*' } },
        responsiveCoverImage: imagePopulate,
      },
    },
  },
  mapProperties: (data) =>
    data.strapi_component === componentName ? data : null,
};

const Headline = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  marginBottom: '20px',
}));

const StyledArticleTeaser = styled(ArticleTeaserMedium)(({ theme }) => ({
  '.article-image': {
    aspectRatio: 335 / 200,

    [theme.breakpoints.up('sm')]: {
      aspectRatio: 484 / 150,
    },

    [theme.breakpoints.up('md')]: {
      aspectRatio: 738 / 200,
    },

    [theme.breakpoints.up('lg')]: {
      aspectRatio: 942 / 250,
    },
  },
}));
